// Phase 38 · 23b — the shell every dashboard widget sits in.
//
// Before this, each widget carried its own copy of the same four states (error,
// loading, empty, content) in `bg-white … rounded-lg shadow`, which is why they
// drifted apart. One shell means a widget author writes the body and the
// finding, and gets the card, the states and the foot band for free.
//
// The foot band is not decoration. A chart that leaves the reader to derive the
// conclusion usually gets no conclusion drawn at all, so 23b requires every
// card to state what it found. `finding` is therefore a first-class prop, and a
// widget that has nothing to say passes nothing rather than filler.

import type { ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { clsx } from 'clsx';
import type { RankedRow } from '../../features/dashboard/widgetFindings';

/** The floating shell: cards sit ON the canvas, they do not butt against it. */
const CARD: React.CSSProperties = { borderRadius: 10, boxShadow: 'var(--co-shadow-card)' };
const CARD_CLASS =
  'flex h-full flex-col overflow-hidden border border-[var(--co-line)] bg-[var(--co-raised)]';

export interface WidgetShellProps {
  title: string;
  subtitle?: string | null;
  /** Top-right control (a link, a range chip). */
  action?: ReactNode;
  /** The foot band: what this card found. Omitted when there is nothing to say. */
  finding?: ReactNode;
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  /** Rendered instead of children when there is no data. */
  empty?: ReactNode;
  /** Skeleton shown while loading. Defaults to a plain block. */
  skeleton?: ReactNode;
  className?: string;
  /** Body padding is the caller's business — charts and lists want different. */
  bodyClassName?: string;
  children?: ReactNode;
}

export function WidgetShell({
  title,
  subtitle,
  action,
  finding,
  loading = false,
  error,
  onRetry,
  empty,
  skeleton,
  className = '',
  bodyClassName = '',
  children,
}: WidgetShellProps) {
  const header = (
    <div className="flex flex-none items-start gap-3 border-b border-[var(--co-line-subtle)] px-[18px] pb-[13px] pt-[15px]">
      <div className="min-w-0 flex-1">
        <h3 className="truncate text-[17px] font-bold leading-[1.15] tracking-[-.018em] text-[var(--co-ink)]">
          {title}
        </h3>
        {subtitle && <p className="mt-1 truncate text-[12.5px] text-[var(--co-ink-3)]">{subtitle}</p>}
      </div>
      {action && <div className="flex-none">{action}</div>}
    </div>
  );

  if (error) {
    return (
      <section className={clsx(CARD_CLASS, className)} style={CARD}>
        {header}
        <div className="flex flex-1 flex-col items-center justify-center px-5 py-8 text-center">
          <AlertTriangle className="mb-3 h-6 w-6 text-[var(--co-burgundy)]" />
          <p className="text-[13px] leading-[1.55] text-[var(--co-ink-3)]">{error}</p>
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="mt-4 flex items-center gap-[7px] rounded-lg border border-[var(--co-line-strong)] px-3 py-[6px] text-[12.5px] text-[var(--co-ink-2)] hover:border-[var(--co-brass)] hover:bg-[var(--co-brass-wash)]"
            >
              <RefreshCw className="h-[13px] w-[13px]" /> Retry
            </button>
          )}
        </div>
      </section>
    );
  }

  const body = loading
    ? skeleton || (
        <div className="space-y-3 p-[18px]">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-8 animate-pulse rounded bg-[var(--co-sunken)]" />
          ))}
        </div>
      )
    : empty || children;

  return (
    <section className={clsx(CARD_CLASS, className)} style={CARD}>
      {header}
      <div className={clsx('min-h-0 flex-1', bodyClassName)}>{body}</div>
      {finding && !loading && (
        <div
          className="flex-none border-t border-[var(--co-line)] bg-[var(--co-selected)] px-[18px] py-3 text-[12.5px] leading-[1.55] text-[var(--co-ink-2)]"
          style={{ borderRadius: '0 0 9px 9px' }}
        >
          {finding}
        </div>
      )}
    </section>
  );
}

/** The bolded clause a foot band opens with. */
export function Found({ children }: { children: ReactNode }) {
  return <strong className="font-semibold text-[var(--co-ink)]">{children}</strong>;
}

/** Centred "nothing here" state for a widget body. */
export function WidgetEmpty({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="flex h-full flex-col items-center justify-center px-5 py-8 text-center">
      <p className="text-[13px] font-semibold text-[var(--co-ink-2)]">{title}</p>
      {hint && <p className="mt-1.5 text-[12.5px] leading-[1.5] text-[var(--co-ink-3)]">{hint}</p>}
    </div>
  );
}

// ---------------------------------------------------------------- ranked list


export interface RankedListProps {
  rows: RankedRow[];
  /** Fallback bar colour when a row carries none. */
  tone?: string;
  className?: string;
}

/**
 * 23b's replacement for the donut. Count, share and ordering are all readable
 * in one pass, and a row holding zero still occupies a line — a category that
 * dropped to nothing is a finding, not an absence.
 */
export function RankedList({ rows, tone = 'var(--co-ink)', className = '' }: RankedListProps) {
  return (
    <div className={clsx('px-[18px]', className)}>
      {rows.map((row, i) => (
        <div
          key={row.key}
          className={clsx('py-[11px]', i < rows.length - 1 && 'border-b border-[var(--co-line-subtle)]')}
        >
          <div className="flex items-baseline gap-[10px]">
            <span
              className="h-[7px] w-[7px] flex-none -translate-y-px rounded-[2px]"
              style={{ background: row.color || tone }}
              aria-hidden
            />
            <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-[var(--co-ink)]">
              {row.label}
            </span>
            <span
              className="co-mono flex-none text-[13px] font-semibold text-[var(--co-ink)]"
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {row.value}
            </span>
            <span
              className="co-mono w-[34px] flex-none text-right text-[10.5px] text-[var(--co-ink-3)]"
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {row.share === null ? '—' : `${Math.round(row.share)}%`}
            </span>
          </div>
          <div className="mt-2 h-[6px] overflow-hidden rounded-[3px] bg-[var(--co-sunken)]">
            <div
              className="h-full rounded-[3px]"
              style={{ width: `${row.weight}%`, background: row.color || tone }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}
